import React from "react";
import clsx from "clsx";

import { IconButton, Menu, MenuItem } from "..";
import useBreakpoints from "../hooks/useBreakpoints";
import MoreHorizontal from "@tiny/icons/generated/MoreHorizontal";
import MoreVertical from "@tiny/icons/generated/MoreVertical";
import Dialog from "../Dialog";
import DialogContent from "../DialogContent";
import SimpleList from "../SimpleList";
import SimpleListItem from "../SimpleListItem";
import { useStyles } from "./MoreButton.styles";
import { MoreButtonProps } from "./MoreButton.types";

function MoreButton<T>(props: MoreButtonProps<T>) {
  const {
    actions,
    clickParam,
    disabled,
    iconMap,
    className,
    variant,
    verticalButton = false,
  } = props;

  const classes = useStyles();

  const { mdUp } = useBreakpoints();

  const moreButtonRef = React.useRef<HTMLButtonElement | null>(null);

  const [moreOpen, setMoreOpen] = React.useState(false);

  const showMore = () => {
    setMoreOpen(true);
  };

  const hideMore = () => {
    setMoreOpen(false);
  };

  const buttonClasses = clsx(className, classes.root, {
    [classes.rootIcon]: variant === "icon",
  });

  return (
    <>
      {verticalButton ? (
        <IconButton
          ref={moreButtonRef}
          onClick={showMore}
          size="small"
          aria-label="More"
        >
          <MoreVertical />
        </IconButton>
      ) : (
        <IconButton
          ref={moreButtonRef}
          className={buttonClasses}
          onClick={showMore}
          size="small"
          aria-label="More"
        >
          <MoreHorizontal />
        </IconButton>
      )}
      {mdUp && moreOpen && (
        <Menu
          anchorEl={moreButtonRef.current}
          keepMounted
          open={moreOpen}
          onClose={hideMore}
          disableScrollLock
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          {actions.map((action) => {
            const visible = action.visible ? action.visible(clickParam) : true;
            if (!visible) {
              return null;
            }
            return (
              <MenuItem
                key={action.name}
                onClick={() => {
                  action.onClickAction(clickParam);
                  hideMore();
                }}
                disabled={disabled}
              >
                {action.name}
              </MenuItem>
            );
          })}
        </Menu>
      )}
      {!mdUp && (
        <Dialog open={moreOpen} onClose={hideMore} disableScrollLock>
          <DialogContent paddingTop={1} paddingBottom={3}>
            <SimpleList>
              {actions.map((action) => (
                <SimpleListItem
                  key={action.name}
                  onClick={() => {
                    action.onClickAction(clickParam);
                    hideMore();
                  }}
                  size="large"
                  icon={iconMap?.[action.name]}
                >
                  {action.name}
                </SimpleListItem>
              ))}
            </SimpleList>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export default React.memo(MoreButton) as unknown as typeof MoreButton;
